import { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/api';
import * as mutations from '../../graphql/mutations';
import useOperationalStore from '../../platform/store/useOperationalStore';

const client = generateClient();

const PARTS_HANDLING_OPTIONS = [
  { value: '',                 label: 'TBD' },
  { value: 'Shop Supplied',    label: 'Shop Supplied' },
  { value: 'Customer Supplied', label: 'Customer Supplied' },
];

export default function NewROModal({
  onClose,
  onSubmit,
  editMode = false,
  truckData = null,
  prefill = null, // create-mode initial values (e.g. converting a road call)
  customerCheckIns = [],
  onCustomerProcessed,
}) {
  const allTrucks     = useOperationalStore(s => s.trucks);
  const technicians   = useOperationalStore(s => s.technicians);

  const [isRoadService, setIsRoadService]   = useState(false);
  const [optionsOpen,   setOptionsOpen]     = useState(false);
  const [companyStatus, setCompanyStatus]   = useState(null);
  const [submitting,    setSubmitting]      = useState(false);

  const [formData, setFormData] = useState({
    repairOrder:       '',
    companyName:       '',
    truckNumber:       '',
    contactPerson:     '',
    phone:             '',
    email:             '',
    year:              '',
    make:              '',
    model:             '',
    vin:               '',
    mileage:           '',
    hours:             '',
    faults:            '',
    reason:            '',
    partsHandling:     '',
    warranty:          false,
    customerCheckInId: null,
    customerType:      '',
    poNumber:          '',
    promisedDate:      '',
    promisedTime:      '',
    techNumber:        '',
    initialNote:       '',
  });

  // Populate form from truck data (edit mode) or prefill (create mode)
  useEffect(() => {
    const source = editMode ? truckData : prefill;
    if (source) {
      let promisedDate = '';
      let promisedTime = '';
      if (source.promisedTime) {
        const d = new Date(source.promisedTime);
        promisedDate = d.toISOString().slice(0, 10);
        promisedTime = d.toTimeString().slice(0, 5);
      }
      setFormData({
        repairOrder:       source.repairOrder   || '',
        companyName:       source.companyName   || '',
        truckNumber:       source.truckNumber   || '',
        contactPerson:     source.contactPerson || '',
        phone:             source.phone         || '',
        email:             source.email         || '',
        year:              source.year          || '',
        make:              source.make          || '',
        model:             source.model         || '',
        vin:               source.vin           || '',
        mileage:           source.mileage       || '',
        hours:             source.hours         || '',
        faults:            source.faults        || '',
        reason:            source.reason        || '',
        partsHandling:     source.partsHandling || '',
        warranty:          source.warranty      || false,
        customerCheckInId: source.customerCheckInId || null,
        customerType:      source.customerType  || '',
        poNumber:          source.poNumber      || '',
        promisedDate,
        promisedTime,
        techNumber:        source.techNumbers?.[0] || source.techNumber || '',
        initialNote:       source.initialNote || '',
      });
    }
  }, [editMode, truckData, prefill]);

  // Auto-detect returning vs. new company
  useEffect(() => {
    if (editMode) return;
    const name = formData.companyName.trim().toLowerCase();
    if (!name) { setCompanyStatus(null); return; }
    const matches = allTrucks.filter(t => t.companyName?.trim().toLowerCase() === name);
    if (matches.length === 0) { setCompanyStatus('new'); return; }
    setCompanyStatus('known');
    const sorted = [...matches].sort((a, b) => (b.startTime || 0) - (a.startTime || 0));
    const knownType = sorted.find(t => t.customerType)?.customerType;
    if (knownType) setFormData(prev => ({ ...prev, customerType: knownType }));
  }, [formData.companyName, allTrucks, editMode]);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }

  function handleCustomerSelect(customer) {
    setFormData(prev => ({
      ...prev,
      companyName:       customer.companyName,
      truckNumber:       customer.truckNumber      || '',
      contactPerson:     customer.contactPerson    || '',
      phone:             customer.phone            || '',
      email:             customer.email            || '',
      year:              customer.vehicleYear      || '',
      make:              customer.vehicleMake      || '',
      model:             customer.vehicleModel     || '',
      vin:               customer.vehicleVin       || '',
      mileage:           customer.vehicleMileage   || '',
      hours:             customer.vehicleEngineHours || '',
      faults:            customer.vehicleFaults    || '',
      reason:            customer.serviceReason    || '',
      partsHandling:     customer.partsHandling    || '',
      customerCheckInId: customer.id,
    }));
    if (onCustomerProcessed) onCustomerProcessed(customer.id);
  }

  async function handleDeleteCheckIn(id) {
    try {
      await client.graphql({ query: mutations.deleteCustomerCheckIn, variables: { input: { id } } });
      if (onCustomerProcessed) onCustomerProcessed(id);
    } catch (err) {
      console.error('Error deleting check-in:', err);
    }
  }

  function buildPromisedTime() {
    if (!formData.promisedDate) return null;
    const str = formData.promisedTime
      ? `${formData.promisedDate}T${formData.promisedTime}`
      : `${formData.promisedDate}T00:00`;
    const ms = new Date(str).getTime();
    return isNaN(ms) ? null : ms;
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    const payload = {
      ...formData,
      isRoadService,
      promisedTime: buildPromisedTime(),
    };
    if (onSubmit) onSubmit(payload);
    // onSubmit is async in callers; don't reset submitting here — modal will close
  }

  const isEdit = editMode;

  return (
    <div className="tt-modal-backdrop">
      <div className="nro-modal tt-modal" style={{ width: 'min(95vw, 520px)', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <div className="tt-modal-header">
          <span className="tt-modal-title">{isEdit ? 'Edit RO' : 'New RO'}</span>
          <button className="tt-modal-close" onClick={onClose} type="button">×</button>
        </div>

        <div className="tt-modal-body" style={{ overflowY: 'auto', flex: 1 }}>
          <form onSubmit={handleSubmit} id="nro-form">

            {/* ── Shop / Road toggle (new RO only) ── */}
            {!isEdit && (
              <div className="nro-service-toggle">
                <button
                  type="button"
                  className={`nro-service-btn${!isRoadService ? ' nro-service-btn--active' : ''}`}
                  onClick={() => setIsRoadService(false)}
                >
                  Shop
                </button>
                <button
                  type="button"
                  className={`nro-service-btn${isRoadService ? ' nro-service-btn--active nro-service-btn--road' : ''}`}
                  onClick={() => setIsRoadService(true)}
                >
                  Road Service
                </button>
              </div>
            )}

            {isRoadService && !isEdit && (
              <div className="nro-road-note">
                For full road service dispatch, open a call in Dispatch after creating this RO.
                Road ROs are created at intake stage.
              </div>
            )}

            {/* ── Check-ins ── */}
            {!isEdit && customerCheckIns.length > 0 && (
              <div className="tt-field">
                <div className="tt-label">
                  Waiting Customers ({customerCheckIns.length})
                  <span className="tt-field-hint" style={{ color: '#94a3b8' }}>— click to pre-fill</span>
                </div>
                <div className="tt-option-list">
                  {customerCheckIns.map(c => (
                    <button key={c.id} type="button" className="tt-option-row" onClick={() => handleCustomerSelect(c)}>
                      <span style={{ flex: 1, fontWeight: 700 }}>{c.companyName}</span>
                      <span style={{ fontSize: 12, color: '#64748b', marginRight: 8 }}>
                        {c.vehicleYear && `${c.vehicleYear} `}{c.vehicleMake}
                      </span>
                      <span
                        role="button"
                        tabIndex={0}
                        className="tt-modal-close"
                        style={{ fontSize: 14, padding: '0 4px' }}
                        onClick={ev => { ev.stopPropagation(); handleDeleteCheckIn(c.id); }}
                        onKeyDown={ev => { if (ev.key === 'Enter') { ev.stopPropagation(); handleDeleteCheckIn(c.id); } }}
                      >
                        ×
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── RO # + Unit ── */}
            <div className="tt-form-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <div className="tt-field" style={{ marginBottom: 0 }}>
                <label>RO # <span className="nro-req">*</span></label>
                <input
                  className="tt-input"
                  name="repairOrder"
                  value={formData.repairOrder}
                  onChange={handleChange}
                  placeholder="e.g. 12345"
                  required
                  autoFocus={!isEdit}
                />
              </div>
              <div className="tt-field" style={{ marginBottom: 0 }}>
                <label>Unit #</label>
                <input
                  className="tt-input"
                  name="truckNumber"
                  value={formData.truckNumber}
                  onChange={handleChange}
                  placeholder="e.g. T-42"
                />
              </div>
            </div>

            {/* ── Company ── */}
            <div className="tt-field" style={{ marginTop: 14 }}>
              <label>
                Company <span className="nro-req">*</span>
                {companyStatus === 'known' && (
                  <span className="tt-field-hint tt-field-hint--good">returning</span>
                )}
                {companyStatus === 'new' && (
                  <span className="tt-field-hint tt-field-hint--warn">new</span>
                )}
              </label>
              <input
                className="tt-input"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                placeholder="Customer or fleet name"
                required
              />
            </div>

            {/* ── Year / Make / Model ── */}
            <div className="tt-form-row" style={{ gridTemplateColumns: '80px 1fr 1fr' }}>
              <div className="tt-field" style={{ marginBottom: 0 }}>
                <label>Year</label>
                <input className="tt-input" name="year" value={formData.year} onChange={handleChange} placeholder="2019" />
              </div>
              <div className="tt-field" style={{ marginBottom: 0 }}>
                <label>Make</label>
                <input className="tt-input" name="make" value={formData.make} onChange={handleChange} placeholder="Peterbilt" />
              </div>
              <div className="tt-field" style={{ marginBottom: 0 }}>
                <label>Model</label>
                <input className="tt-input" name="model" value={formData.model} onChange={handleChange} placeholder="389" />
              </div>
            </div>

            <div className="tt-field" style={{ marginTop: 10 }}>
              <label>VIN</label>
              <input className="tt-input" name="vin" value={formData.vin} onChange={handleChange} placeholder="17-character VIN" />
            </div>

            {/* ── Complaint ── */}
            <div className="tt-field">
              <label className="nro-complaint-label">
                Complaint <span className="nro-req">*</span>
              </label>
              <textarea
                className="tt-textarea tt-input"
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                placeholder="Describe the reason for visit…"
                rows={3}
                required
                style={{ resize: 'vertical' }}
              />
            </div>

            {/* ── Contact ── */}
            <div className="tt-form-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <div className="tt-field" style={{ marginBottom: 0 }}>
                <label>Contact Name</label>
                <input className="tt-input" name="contactPerson" value={formData.contactPerson} onChange={handleChange} placeholder="Driver or fleet mgr" />
              </div>
              <div className="tt-field" style={{ marginBottom: 0 }}>
                <label>Phone</label>
                <input className="tt-input" name="phone" value={formData.phone} onChange={handleChange} placeholder="(555) 000-0000" type="tel" />
              </div>
            </div>

            {/* ── Optional section ── */}
            <button
              type="button"
              className="nro-optional-toggle"
              onClick={() => setOptionsOpen(o => !o)}
            >
              {optionsOpen ? '▾ Additional Details' : '▸ Additional Details +'}
            </button>

            {optionsOpen && (
              <div className="nro-optional-body">

                <div className="tt-form-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                  <div className="tt-field" style={{ marginBottom: 0 }}>
                    <label>Mileage</label>
                    <input className="tt-input" name="mileage" value={formData.mileage} onChange={handleChange} placeholder="e.g. 312400" />
                  </div>
                  <div className="tt-field" style={{ marginBottom: 0 }}>
                    <label>Hours</label>
                    <input className="tt-input" name="hours" value={formData.hours} onChange={handleChange} placeholder="e.g. 8210" />
                  </div>
                </div>

                <div className="tt-form-row" style={{ gridTemplateColumns: '1fr 1fr', marginTop: 12 }}>
                  <div className="tt-field" style={{ marginBottom: 0 }}>
                    <label>Promise Date</label>
                    <input className="tt-input" name="promisedDate" value={formData.promisedDate} onChange={handleChange} type="date" />
                  </div>
                  <div className="tt-field" style={{ marginBottom: 0 }}>
                    <label>Promise Time</label>
                    <input className="tt-input" name="promisedTime" value={formData.promisedTime} onChange={handleChange} type="time" />
                  </div>
                </div>

                {technicians.length > 0 && (
                  <div className="tt-field" style={{ marginTop: 12 }}>
                    <label>Assign Tech</label>
                    <select className="tt-input" name="techNumber" value={formData.techNumber} onChange={handleChange}>
                      <option value="">— Unassigned —</option>
                      {technicians.map(t => (
                        <option key={t.number || t.id} value={t.number || t.id}>
                          {t.number ? `#${t.number} — ` : ''}{t.name || t.number}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Fleets and municipalities won't pay an invoice that is
                    missing their PO. Optional here — it often arrives by phone
                    after the truck is already in the yard — and editable on the
                    job right up until the invoice goes out. */}
                <div className="tt-field" style={{ marginTop: 12 }}>
                  <label>Customer PO #</label>
                  <input
                    className="tt-input"
                    name="poNumber"
                    value={formData.poNumber}
                    onChange={handleChange}
                    placeholder="Their purchase order number"
                  />
                </div>

                <div className="tt-field" style={{ marginTop: 12 }}>
                  <label>Parts Handling</label>
                  <select className="tt-input" name="partsHandling" value={formData.partsHandling} onChange={handleChange}>
                    {PARTS_HANDLING_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>

                <label className="tt-checkbox-row" style={{ marginTop: 12 }}>
                  <input type="checkbox" name="warranty" checked={formData.warranty} onChange={handleChange} />
                  <span>Warranty Work</span>
                </label>

                <div className="tt-field" style={{ marginTop: 12 }}>
                  <label>Initial Note</label>
                  <textarea
                    className="tt-textarea tt-input"
                    name="initialNote"
                    value={formData.initialNote}
                    onChange={handleChange}
                    placeholder="First activity note on this RO (optional)…"
                    rows={2}
                    style={{ resize: 'vertical' }}
                  />
                </div>

              </div>
            )}

          </form>
        </div>

        {/* Footer */}
        <div className="tt-modal-footer" style={{ flexShrink: 0 }}>
          <button type="button" className="tt-button tt-button--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            form="nro-form"
            className="tt-button tt-button--primary"
            disabled={submitting}
          >
            {isEdit ? 'Update RO' : 'Create RO'}
          </button>
        </div>

      </div>
    </div>
  );
}
